import { useUserSettings } from '../../../../../hooks/api/user-settings';
import { AccessMap } from '@repo/ui/features/inventaires/arbre/intervention/components/AccessMap';
import type { PrintDataFields } from '@repo/types';
import { imageService } from '@repo/ui/lib/services';
import EssenceInterventionSection from './EssenceInterventionSection';
import { formatDate } from '../../../../../lib/utils';

export type EpaysageInterventionPrintData = PrintDataFields;

interface InterventionFormEpaysageProps {
  readonly printData: EpaysageInterventionPrintData;
  readonly inventaireId: number;
}

interface EssenceHeaderInfo {
  label: string;
  startDate: string;
  endDate: string;
}

export default function InterventionFormEpaysage({
  printData,
  inventaireId,
}: InterventionFormEpaysageProps) {
  const { data: userSettingsData } = useUserSettings();


  const defaultStartTime = '08:30';
  const defaultEndTime = '17:00';

  const organizationLogo = printData.organizationLogo
    ? imageService.getImageUrl(printData.organizationLogo, 'logo')
    : null;

  const parseGPS = (gps: string): { lat: number; lng: number } | undefined => {
    if (!gps) return undefined;
    const parts = gps
      .split(',')
      .map((p) => p.trim())
      .filter((p) => p.length > 0);
    if (parts.length >= 2) {
      const latStr: string | undefined = parts[0];
      const lngStr: string | undefined = parts[1];
      if (latStr !== undefined && lngStr !== undefined) {
        const lat = Number.parseFloat(latStr);
        const lng = Number.parseFloat(lngStr);
        if (!Number.isNaN(lat) && !Number.isNaN(lng)) {
          return { lat, lng };
        }
      }
    }
    return undefined;
  };

  const coord = parseGPS(printData.gps as string);
  const addressLine = printData.addressLine || '';

  const essences: any[] =
    'essence' in printData && Array.isArray((printData as any).essence)
      ? ((printData as any).essence as any[])
      : [];

  const buildEssenceHeaderInfos = (): EssenceHeaderInfo[] => {
    const maxEssences = 5;
    const result: EssenceHeaderInfo[] = [];

    for (let i = 0; i < maxEssences; i += 1) {
      const essence = essences[i];
      if (!essence) {
        continue;
      }

      const essenceName =
        essence.espece?.nom_fr ||
        essence.espece?.name ||
        `Essence ${i + 1}`;

      const travauxDateRaw: string | undefined =
        essence.user_edited_date_travaux || essence.date_travaux;

      const interventionDate = travauxDateRaw || printData.formattedDate || '';
      const travauxDate = travauxDateRaw || printData.formattedDate || '';

      result.push({
        label: `${essenceName}`,
        startDate: interventionDate,
        endDate: travauxDate,
      });
    }

    return result;
  };

  const essenceHeaders = buildEssenceHeaderInfos();
  const essencesLabel = essences?.map((essence: any) => essence.espece?.nom_fr).join(', ');

  const interventionNumber = inventaireId || printData.code_site || '';
  const clientName = printData.code_site || '';
  const globalMilieu =
    'milieu' in printData.caracteristiques
      ? (printData.caracteristiques as any).milieu || ''
      : '';
  const globalSol =
    'sol' in printData.caracteristiques
      ? (printData.caracteristiques as any).sol || ''
      : '';
  const globalRisque = printData.risque?.general || '';
  const globalComments = printData.comments || '';
  const globalNuisance = printData.environnement?.nuisance || '';
  const globalSituation =
    'situation' in printData.environnement
      ? (printData.environnement as any).situation || ''
      : '';

  const intervenersList = userSettingsData?.data?.list_intervenant || '';
  const materielList = userSettingsData?.data?.list_materiel || [];

  const materielArray = Array.isArray(materielList) ? materielList : [];
  const intervenersArray = intervenersList
    ? intervenersList.split('\n').filter((line: string) => line.trim())
    : [];

  const buildTravauxDescription = (essence: any): string => {
    if (!essence) {
      return '';
    }
    if (essence.travaux_com) {
      return String(essence.travaux_com);
    }
    if (essence.travaux) {
      return String(essence.travaux);
    }
    return '';
  };

  const buildDefautsMecaniques = (essence: any): string => {
    if (!essence) {
      return globalRisque;
    }
    return essence.etat_san_general || globalRisque;
  };

  const buildProximiteDirecte = (essence: any): string => {
    if (essence?.proximite) {
      return String(essence.proximite);
    }
    return globalMilieu || globalNuisance;
  };

  const buildNuisancesConstatees = (essence: any): string => {
    if (essence?.travaux_com) {
      return String(essence.travaux_com);
    }
    return globalSituation;
  };

  return (
    <div className="min-h-screen bg-white p-8 print:p-4">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-start justify-between mb-6">
          <div className="border border-gray-300 p-4 w-[200px] h-[200px] flex items-center justify-center">
            {organizationLogo ? (
              <img
                src={organizationLogo}
                alt="Logo entreprise"
                className="max-h-full max-w-full object-contain"
              />
            ) : (
              <div className="text-xs text-gray-400">Logo</div>
            )}
          </div>
          <div className="flex-1 flex flex-col items-center space-y-4">
            <h1 className="text-3xl font-bold text-black text-center">
              FICHE D&apos;INTERVENTION {(printData as any)?.type_espace}
            </h1>

          </div>
          <div className="w-40" />
        </div>

        <div className="space-y-4 mb-6">
          <div className="border-2 border-black p-4">
            <div className="space-y-2 text-sm">
              <div className="flex">
                <span className="font-bold">Numéro d&apos;intervention :</span>
                <span className="border-b border-black flex-1 ml-2">
                  {interventionNumber}
                </span>
              </div>
              <div className="flex">
                <span className="font-bold">
                  Date et heure de début :
                </span>
                <span className="border-b border-black flex-1 ml-2">
                  {printData.formattedDate} à {defaultStartTime}
                </span>
              </div>
              <div className="flex">
                <span className="font-bold">
                  Date et heure de fin  :
                </span>
                <span className="border-b border-black flex-1 ml-2">
                  {printData.formattedDate} à {defaultEndTime}
                </span>
              </div>
            </div>



            <div className="space-y-2 text-sm mt-2">
              <div className="flex">
                <span className="font-bold">Nom du client :</span>
                <span className="border-b border-black flex-1 ml-2">
                  {clientName}
                </span>
              </div>
              <div className="flex">
                <span className="font-bold">Adresse :</span>
                <span className="border-b border-black flex-1 ml-2">
                  {addressLine}
                </span>
              </div>
              <div className="mt-2">
                <div className="flex items-baseline">
                  <span className="font-bold">Description :</span>
                  <span className="border-b border-black flex-1 ml-2" />
                </div>
                <div className="ml-2 mt-2 space-y-1 text-sm">
                  <div className="flex items-baseline gap-2">
                    <span>-</span>
                    <span>Milieu :</span>
                    <span className="border-b border-black flex-1">
                      {globalMilieu}
                    </span>
                  </div>
                  <div className="flex items-baseline gap-2">
                    <span>-</span>
                    <span>Sol :</span>
                    <span className="border-b border-black flex-1">
                      {globalSol}
                    </span>
                  </div>
                  <div className="flex items-baseline gap-2">
                    <span>-</span>
                    <span>Risque global :</span>
                    <span className="border-b border-black flex-1">
                      {globalRisque}
                    </span>
                  </div>
                  <div className="flex items-baseline gap-2">
                    <span>-</span>
                    <span>Superficie :</span>
                    <span className="border-b border-black flex-1">
                      {(printData as any).surface || ''}
                    </span>
                  </div>
                  <div className="flex items-baseline gap-2">
                    <span>-</span>
                    <span>Essences :</span>
                    <span className="border-b border-black flex-1">
                      {essencesLabel}
                    </span>
                  </div>
                </div>
                <div className="mt-4">
                  <div className="font-bold underline mb-2">
                    Commentaires :
                  </div>
                  <div className="ml-2">
                    {globalComments}
                  </div>
                </div>
              </div>


            </div>
          </div>
        </div>

        <div className="mb-2">
          <div className="font-bold  mb-4">Travaux :</div>

        </div>
        {essences.slice(0, 5).map((essence, index) => {
          const travauxDescription = buildTravauxDescription(essence);
          const defautsMecaniques = buildDefautsMecaniques(essence);
          const proximiteDirecte = buildProximiteDirecte(essence);
          const nuisancesConstatees = buildNuisancesConstatees(essence);

          return (
            <EssenceInterventionSection
              key={essence?.id ?? `essence-${index}`}
              index={index}
              travauxDescription={travauxDescription}
              defautsMecaniques={defautsMecaniques}
              proximiteDirecte={proximiteDirecte}
              nuisancesConstatees={nuisancesConstatees}
              travauxAbattage={essence?.travaux_abattage}
              travauxDemontage={essence?.travaux_demontage}
              travauxMiseEnSecurite={essence?.travaux_mise_en_securite}
              travauxTailleSanitaire={essence?.travaux_taille_sanitaire}
              travauxDechiffrage={essence?.travaux_dechiffrage}
              travauxCoupleSelective={essence?.travaux_couple_selective}
            />
          );
        })}

        <div className="flex justify-center mb-6">
          <img
            src="/images/numeros-urgence-1.jpg"
            alt="Numéros d'urgence"
            className="w-full max-w-3xl"
          />
        </div>

        <div className="border-2 border-black mb-6 print:page-break-after-always">
          <table className="w-full">
            <thead>
              <tr className="bg-black text-white">
                <th className="py-2 px-4 text-center font-bold border-r border-white">
                  Equipement
                </th>
                <th className="py-2 px-4 text-center font-bold border-r border-white">
                  Modèle
                </th>
                <th className="py-2 px-4 text-center font-bold">Type</th>
              </tr>
            </thead>
            <tbody>
              {materielArray.length > 0 ? (
                materielArray.slice(0, 8).map((item, index) => {
                  const materielItem =
                    typeof item === 'object' &&
                      item !== null &&
                      'equipement' in item
                      ? (item as {
                        equipement: string;
                        modele: string;
                        type: string;
                      })
                      : { equipement: '', modele: '', type: '' };
                  return (
                    <tr key={index} className="border-t border-black">
                      <td className="py-4 px-4 border-r border-black">
                        {materielItem.equipement || ''}
                      </td>
                      <td className="py-4 px-4 border-r border-black">
                        {materielItem.modele || ''}
                      </td>
                      <td className="py-4 px-4">
                        {materielItem.type || ''}
                      </td>
                    </tr>
                  );
                })
              ) : (
                new Array(8).fill(null).map((_, index) => (
                  <tr key={`materiel-${index}`} className="border-t border-black">
                    <td className="py-4 px-4 border-r border-black" />
                    <td className="py-4 px-4 border-r border-black" />
                    <td className="py-4 px-4" />
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {coord && (
          <div className="border-2 border-black p-4 mb-6 print:page-break-before-auto print:page-break-inside-avoid">
            <div className="font-bold underline mb-4">Plan d&apos;accès :</div>
            <div className="border border-gray-300 p-2 print:overflow-hidden print:relative">
              <AccessMap coordinates={coord} address={addressLine || undefined} />
            </div>
          </div>
        )}

        <div className="border-2 border-black p-4 mb-6 min-h-[200px]">
          <div className="font-bold underline mb-4">
            Liste des intervenants :
          </div>
          <div className="space-y-3">
            {intervenersArray.length > 0
              ? intervenersArray.slice(0, 5).map((intervener, index) => (
                <div
                  key={`${intervener}-${index}`}
                  className="border-b border-black h-6"
                >
                  {intervener}
                </div>
              ))
              : new Array(5).fill(null).map((_, index) => (
                <div
                  key={`intervenant-${index}`}
                  className="border-b border-black h-6"
                />
              ))}
          </div>
        </div>
      </div>


    </div>
  );
}

